import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";

const applySearchParamUpdate = (current, key, value, defaultValue) => {
  const newSearchParams = new URLSearchParams(current);
  if (value === defaultValue || value === null || value === "") {
    newSearchParams.delete(key);
  } else {
    newSearchParams.set(key, value);
  }
  return newSearchParams;
};

const applySearchParamsUpdates = (current, updates, defaultStates) => {
  const newSearchParams = new URLSearchParams(current);
  Object.keys(updates).forEach((key) => {
    const value = updates[key];
    if (value === defaultStates[key] || value === null || value === "") {
      newSearchParams.delete(key);
    } else {
      newSearchParams.set(key, value);
    }
  });
  return newSearchParams;
};

/**
 * Hook para manejar el estado de la URL de manera persistente
 * Permite sincronizar el estado del componente con los parámetros de la URL
 */
export const useUrlState = (key, defaultValue = null) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const searchParamsRef = useRef(new URLSearchParams(location.search));
  const [value, setValue] = useState(() => {
    const urlValue = searchParams.get(key);
    return urlValue !== null ? urlValue : defaultValue;
  });

  useEffect(() => {
    searchParamsRef.current = new URLSearchParams(location.search);
  }, [location.search]);

  useEffect(() => {
    const urlValue = searchParams.get(key);
    if (urlValue !== null && urlValue !== value) {
      setValue(urlValue);
    }
  }, [searchParams, key, value]);

  const updateValue = useCallback(
    (newValue) => {
      setValue(newValue);

      const newSearchParams = applySearchParamUpdate(
        searchParamsRef.current,
        key,
        newValue,
        defaultValue,
      );
      searchParamsRef.current = newSearchParams;
      setSearchParams(newSearchParams, { replace: true });
    },
    [defaultValue, key, setSearchParams],
  );

  return [value, updateValue];
};

/**
 * Hook para manejar múltiples estados de URL
 */
export const useUrlStates = (initialStates = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const defaultStatesRef = useRef(initialStates);
  const searchParamsRef = useRef(new URLSearchParams(location.search));
  defaultStatesRef.current = initialStates;

  const [states, setStates] = useState(() => {
    const initial = {};
    Object.keys(initialStates).forEach((key) => {
      const urlValue = searchParams.get(key);
      initial[key] = urlValue !== null ? urlValue : initialStates[key];
    });
    return initial;
  });

  useEffect(() => {
    searchParamsRef.current = new URLSearchParams(location.search);
  }, [location.search]);

  const commitSearchParams = useCallback(
    (newSearchParams) => {
      searchParamsRef.current = newSearchParams;
      setSearchParams(newSearchParams, { replace: true });
    },
    [setSearchParams],
  );

  const updateState = useCallback(
    (key, value) => {
      setStates((prev) => ({ ...prev, [key]: value }));

      const newSearchParams = applySearchParamUpdate(
        searchParamsRef.current,
        key,
        value,
        defaultStatesRef.current[key],
      );
      commitSearchParams(newSearchParams);
    },
    [commitSearchParams],
  );

  const updateStates = useCallback(
    (updates) => {
      setStates((prev) => ({ ...prev, ...updates }));

      const newSearchParams = applySearchParamsUpdates(
        searchParamsRef.current,
        updates,
        defaultStatesRef.current,
      );
      commitSearchParams(newSearchParams);
    },
    [commitSearchParams],
  );

  return [states, updateState, updateStates];
};
